import React, { useState, useEffect, useCallback } from 'react';
import { dashboardService } from '../services/api';
import { GlassCard, GlassButton } from '../components/GlassUI';
import { segmentMeta } from '../constants/segments';
import { toCsv, downloadCsv, formatDate, todayStamp } from '../utils/download';
import {
  FaArrowLeft, FaUser, FaPhone, FaWhatsapp, FaShoppingBag, FaClock, FaDownload
} from 'react-icons/fa';

interface SegmentDetailPageProps {
  segmentType: string;
  onBack: () => void;
}

export const SegmentDetailPage: React.FC<SegmentDetailPageProps> = ({ segmentType, onBack }) => {
  const [customers, setCustomers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [limit] = useState(20);

  const meta = segmentMeta(segmentType);

  const handleDownload = async () => {
    setExporting(true);
    try {
      const response = await dashboardService.getSegmentCustomersForExport(segmentType);
      const rows: any[] = response.data.customers || [];
      const csv = toCsv(rows, [
        { header: 'Name', value: (r) => r.customerName },
        { header: 'Phone', value: (r) => r.cellNumber },
        { header: 'WhatsApp', value: (r) => r.whatsappNumber },
        { header: 'Location', value: (r) => r.location },
        { header: 'Celebration', value: (r) => r.celebrationType },
        { header: 'Purchases', value: (r) => r.purchaseCount ?? r.totalLifetimeOrders ?? 0 },
        { header: 'Last Purchase', value: (r) => formatDate(r.lastPurchaseDate) },
        { header: 'Last Interaction', value: (r) => formatDate(r.lastInteractionDate) },
        { header: 'Last Follow-up', value: (r) => formatDate(r.lastFollowUp) },
        { header: 'Lifetime Billing', value: (r) => r.lifetimeBilling ?? 0 },
        { header: 'Lifetime Gold (g)', value: (r) => r.lifetimeGoldWeight ?? 0 },
      ]);
      downloadCsv(`${segmentType}-customers-${todayStamp()}.csv`, csv);
    } catch (error) {
      console.error('Export failed:', error);
      alert('Could not build the download. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  const loadSegmentCustomers = useCallback(async () => {
    setLoading(true);
    try {
      const response = await dashboardService.getSegmentCustomers(segmentType, page, limit);
      setCustomers(response.data.customers || []);
      setTotal(response.data.pagination?.total || 0);
    } catch (error) {
      console.error('Error loading segment customers:', error);
    } finally {
      setLoading(false);
    }
  }, [segmentType, page, limit]);

  useEffect(() => {
    loadSegmentCustomers();
  }, [loadSegmentCustomers]);

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="max-w-5xl mx-auto">
      <div className="mb-6">
        <GlassButton variant="secondary" onClick={onBack} className="mb-4 text-sm flex items-center gap-2">
          <FaArrowLeft /> Back to Dashboard
        </GlassButton>
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <span className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-sm font-semibold ${meta.badgeClass}`}>
              {meta.label}
            </span>
            <span className="text-slate-500 text-sm">{total} customer{total !== 1 ? 's' : ''}</span>
          </div>
          <GlassButton
            variant="secondary"
            className="text-sm flex items-center gap-2"
            onClick={handleDownload}
            disabled={exporting || total === 0}
          >
            <FaDownload size={12} /> {exporting ? 'Preparing…' : 'Download CSV'}
          </GlassButton>
        </div>
        {meta.description && (
          <p className="text-slate-500 text-sm mt-2">{meta.description}</p>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center min-h-[40vh]">
          <div className="text-center">
            <div className="animate-spin w-8 h-8 border-4 border-slate-300 border-t-slate-900 rounded-full mx-auto mb-4" />
            <p className="text-slate-500">Loading customers...</p>
          </div>
        </div>
      ) : customers.length === 0 ? (
        <GlassCard className={`${meta.bgClass}`}>
          <p className="text-slate-500 text-center py-8">No customers in this segment</p>
        </GlassCard>
      ) : (
        <div className="space-y-3">
          {customers.map((customer: any) => (
            <GlassCard key={customer.id} className="hover:shadow-md transition-shadow">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3 min-w-0 flex-1">
                  <div className="w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center shrink-0">
                    <FaUser className="text-slate-500" size={16} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-slate-900 truncate">{customer.customerName || 'N/A'}</p>
                    <div className="flex items-center gap-3 mt-0.5">
                      {customer.cellNumber && (
                        <span className="flex items-center gap-1 text-xs text-slate-500">
                          <FaPhone size={10} /> {customer.cellNumber}
                        </span>
                      )}
                      {customer.location && (
                        <span className="flex items-center gap-1 text-xs text-slate-400 truncate">
                          {customer.location}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0 ml-3 flex-wrap justify-end">
                  {customer.celebrationType && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-pink-100 text-pink-800">
                      {customer.celebrationType === 'BIRTHDAY' ? '🎂 Birthday' : '💍 Anniversary'}
                    </span>
                  )}
                  {(customer.purchaseCount ?? customer.totalLifetimeOrders) > 0 && (
                    <span className="flex items-center gap-1 text-xs text-slate-500">
                      <FaShoppingBag size={10} /> {customer.purchaseCount ?? customer.totalLifetimeOrders}
                    </span>
                  )}
                  {customer.lastPurchaseDate ? (
                    <span className="flex items-center gap-1 text-xs text-slate-400" title="Last purchase">
                      <FaClock size={10} /> {formatDate(customer.lastPurchaseDate)}
                    </span>
                  ) : customer.lastInteractionDate ? (
                    <span className="flex items-center gap-1 text-xs text-slate-400" title="Last interaction">
                      <FaClock size={10} /> {formatDate(customer.lastInteractionDate)}
                    </span>
                  ) : null}
                  {customer.lifetimeBilling > 0 && (
                    <span className="text-xs text-slate-500">₹{Number(customer.lifetimeBilling).toLocaleString('en-IN')}</span>
                  )}
                  {customer.whatsappNumber && (
                    <FaWhatsapp className="text-green-500" size={16} />
                  )}
                </div>
              </div>
            </GlassCard>
          ))}
        </div>
      )}

      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 mt-6">
          <GlassButton
            variant="secondary"
            className="text-sm"
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1}
          >
            Previous
          </GlassButton>
          <span className="text-sm text-slate-600">Page {page} of {totalPages}</span>
          <GlassButton
            variant="secondary"
            className="text-sm"
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
          >
            Next
          </GlassButton>
        </div>
      )}
    </div>
  );
};

export default SegmentDetailPage;
